import type { ISpeedDataMarkResponse } from "@framework/types";

import { unwrapPhaseDegrees } from "../../../scheme/components/analytics/utils/amplitude-phase";
import type {
  IMultiAnalysisDataMark,
  IMultiAnalysisTimeRange,
  IStructureTreePoint,
} from "../../../multi-analysis/types";

export const BODE_MEASURE_TYPE = 4;
export const BODE_PHASE_UNIT_CODE = 133;
export const BODE_FREQUENCY_HZ_UNIT_CODE = 3;
export const BODE_ROTATION_FREQUENCY_UNIT_CODE = 140;

export interface IBodeControlParameter {
  title: string;
  units: string;
  value: number;
  isRotationFrequency: boolean;
}

export interface IBodeDataMark extends IMultiAnalysisDataMark {
  bode: { controlParameter: IBodeControlParameter | null };
}

export interface IBodeSample {
  key: string;
  date: string;
  amplitude: number;
  phaseDegrees: number | null;
  unwrappedPhaseDegrees: number | null;
  rotationFrequencyRpm: number | null;
}

export interface IBodeSeries {
  key: string;
  pointId: number;
  measureId: number;
  name: string;
  pointName: string;
  measureName: string;
  unitCode: number;
  units: string;
  color: string;
  samples: readonly IBodeSample[];
}

export const parseBodeWallClock = (value: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = "0", fraction = "0"] = match;
  const timestamp = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number(fraction.padEnd(3, "0")),
  );
  const parsed = new Date(timestamp);
  return parsed.getUTCFullYear() === Number(year) &&
    parsed.getUTCMonth() === Number(month) - 1 &&
    parsed.getUTCDate() === Number(day) &&
    parsed.getUTCHours() === Number(hour) &&
    parsed.getUTCMinutes() === Number(minute) &&
    parsed.getUTCSeconds() === Number(second)
    ? timestamp
    : null;
};

export const isBodeDataMark = (mark: IMultiAnalysisDataMark): mark is IBodeDataMark => "bode" in mark;

export const mapBodeDataMarks = (
  rows: readonly ISpeedDataMarkResponse[],
  points: readonly IStructureTreePoint[],
  formatDirection: (direction: number) => string,
  controlParameter: { dataId: number; title: string; units: string } | null,
): IBodeDataMark[] => {
  const pointsById = new Map(points.map(point => [point.id, point]));
  const marks = new Map<string, IBodeDataMark>();

  for (const row of rows) {
    const point = pointsById.get(row.idPoint);
    if (
      !point ||
      row.Type !== BODE_MEASURE_TYPE ||
      !Number.isFinite(row.Value1) ||
      parseBodeWallClock(row.Date) === null
    )
      continue;

    const key = `${row.idPoint}:${row.idMeasure}:${row.Date}`;
    const direction = Number.isFinite(point.direction) ? formatDirection(point.direction).trim() : "";
    const directionLabel = direction.startsWith("(") && direction.endsWith(")") ? direction : `(${direction})`;
    marks.set(key, {
      key,
      idMeasure: row.idMeasure,
      idPoint: row.idPoint,
      date: row.Date,
      measureDate: row.Date,
      pointName: direction ? `${point.title} ${directionLabel}` : point.title,
      measureName: row.Name,
      measureDescription: row.Description,
      value: row.Value1,
      value2: row.Value2 !== null && Number.isFinite(row.Value2) ? row.Value2 : null,
      units: row.Units,
      alarmStatus: row.AlarmStatus,
      rotationFrequencyRpm: row.RPM !== null && Number.isFinite(row.RPM) ? row.RPM : null,
      param1: row.Param1,
      param2: row.Param2,
      bode: {
        controlParameter:
          controlParameter && row.ControlValue !== null && Number.isFinite(row.ControlValue)
            ? {
                title: controlParameter.title,
                units: controlParameter.units,
                value: row.ControlValue,
                isRotationFrequency: row.Key01 !== null && controlParameter.dataId === row.Key01,
              }
            : null,
      },
    });
  }

  return [...marks.values()].sort(
    (left, right) =>
      parseBodeWallClock(left.date)! - parseBodeWallClock(right.date)! || left.key.localeCompare(right.key),
  );
};

export const getBodeFrameMarks = (
  marks: readonly IMultiAnalysisDataMark[],
  frameRange: IMultiAnalysisTimeRange,
): IBodeDataMark[] => {
  const parseBound = (value: IMultiAnalysisTimeRange["start"]) =>
    value ? parseBodeWallClock(value instanceof Date ? value.toISOString() : value) : null;
  const start = parseBound(frameRange.start);
  const end = parseBound(frameRange.end);
  if (start === null || end === null) return [];
  const lower = Math.min(start, end);
  const upper = Math.max(start, end);
  return marks.filter(isBodeDataMark).filter(mark => {
    const timestamp = parseBodeWallClock(mark.date);
    return timestamp !== null && timestamp >= lower && timestamp <= upper;
  });
};

export const buildBodeSeries = (
  marks: readonly IBodeDataMark[],
  getUnits: (unitCode: number) => string,
  getColor: (key: string) => string,
): IBodeSeries[] => {
  const grouped = new Map<
    string,
    Omit<IBodeSeries, "samples"> & { samples: Omit<IBodeSample, "unwrappedPhaseDegrees">[] }
  >();
  for (const mark of marks) {
    const key = `${mark.idPoint}:${mark.idMeasure}:${mark.units}`;
    const series = grouped.get(key) ?? {
      key,
      pointId: mark.idPoint,
      measureId: mark.idMeasure,
      name: `${mark.pointName} / ${mark.measureName}`,
      pointName: mark.pointName,
      measureName: mark.measureName,
      unitCode: mark.units,
      units: getUnits(mark.units),
      color: getColor(key),
      samples: [],
    };
    series.samples.push({
      key: mark.key,
      date: mark.date,
      amplitude: mark.value,
      phaseDegrees: mark.value2,
      rotationFrequencyRpm: mark.rotationFrequencyRpm,
    });
    grouped.set(key, series);
  }

  return [...grouped.values()].map(series => {
    const samples = series.samples
      .slice()
      .sort((left, right) => parseBodeWallClock(left.date)! - parseBodeWallClock(right.date)!);
    const phases = unwrapPhaseDegrees(samples.map(sample => sample.phaseDegrees ?? Number.NaN));
    return {
      ...series,
      samples: samples.map((sample, index) => ({
        ...sample,
        unwrappedPhaseDegrees: Number.isFinite(phases[index]) ? phases[index] : null,
      })),
    };
  });
};

export const resolveBodeCursor = (marks: readonly IBodeDataMark[], cursorKey: string | null): IBodeDataMark | null =>
  marks.find(mark => mark.key === cursorKey) ?? marks[marks.length - 1] ?? null;

export const formatBodeUnitLabel = (label: string): string => label.slice(label.lastIndexOf(",") + 1).trim();

export const formatBodeDate = (date: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}:\d{2}:\d{2})/u.exec(date);
  return match ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}` : date;
};

export const formatBodeHeader = (
  mark: IBodeDataMark,
  amplitudeUnits: string,
  phaseUnits: string,
  frequencyUnits: string,
  formatValue: (value: number, unitCode: number | null) => string,
  frequencyValueFactor = 1,
  frequencyUnitCode = BODE_ROTATION_FREQUENCY_UNIT_CODE,
): string => {
  const control = mark.bode.controlParameter;
  const parts = [mark.pointName, mark.measureName, formatBodeDate(mark.date)];
  if (control) parts.push(`${control.title}: ${formatValue(control.value, null)} ${control.units}`.trim());
  if (!control?.isRotationFrequency && mark.rotationFrequencyRpm !== null)
    parts.push(
      `${formatValue(mark.rotationFrequencyRpm * frequencyValueFactor, frequencyUnitCode)} ${frequencyUnits}`.trim(),
    );
  const values = `${formatValue(mark.value, mark.units)} ${amplitudeUnits} / ${
    mark.value2 === null ? "—" : formatValue(mark.value2, BODE_PHASE_UNIT_CODE)
  } ${phaseUnits}`.trim();
  return `${parts.join(" / ")} : ${values}`;
};
